/**
 * useViewTracking
 *
 * Tracks views for products, services, companies and user profiles.
 *
 * Spam prevention — two layers:
 *   1. Frontend (this file): a module-level Set keeps every "type:id" key that
 *      has already been sent in the current browser tab session.  The API call
 *      is skipped entirely if the key is already in the Set — zero HTTP traffic.
 *
 *   2. Backend (Viewable trait): even if a call gets through (e.g. new tab, page
 *      refresh) the trait checks the DB and discards duplicates within a 60-minute
 *      window per user/IP.
 *
 * Usage:
 *   const { track } = useViewTracking()
 *   track('product', 42)
 *   track('service', 7)
 *   track('company', 3)
 *   track('user', 11)
 */

import { record } from '@/api/views'

// Shared across every component that imports this composable in the same tab.
// Cleared only when the page is refreshed or the tab is closed.
const tracked = new Set()

export function useViewTracking() {
    /**
     * Record a view for the given entity — no-op if already tracked this session.
     *
     * @param {'product'|'company'|'service'|'user'} type
     * @param {number|string} id
     */
    function track(type, id) {
        if (!type || !id) return

        const key = `${type}:${id}`
        if (tracked.has(key)) return   // already sent this session — skip

        // Mark immediately so rapid re-calls (before the response arrives) are
        // also blocked.
        tracked.add(key)

        // Fire and forget — errors don't matter for view tracking.
        record(type, id).catch(() => {})
    }

    return { track }
}
